import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Plus, Search, Receipt, CalendarIcon, Trash2, Eye, Edit } from 'lucide-react';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';


const Operations = () => {
  const { t, API, canEdit, isAdmin } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const cabinetIdFromUrl = searchParams.get('cabinet_id');
  const actionFromUrl = searchParams.get('action'); 
  const editIdFromUrl = searchParams.get('operation_id');

  const [operations, setOperations] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [users, setUsers] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCabinet, setFilterCabinet] = useState(cabinetIdFromUrl || 'all');

  const [showCreateDialog, setShowCreateDialog] = useState(
    (cabinetIdFromUrl && actionFromUrl === 'create') || (editIdFromUrl && actionFromUrl === 'edit')
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [formData, setFormData] = useState({
    cabinet_id: cabinetIdFromUrl || '',
    operation_type: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    price: '',
    estimated_cost: '',
    transport_cost: '',
    price_breakdown: [],
    affected_components: [],
  });

  useEffect(() => {
    fetchOperations();
    fetchCabinets();
    fetchUsers();
    fetchComponents();
  }, []);

  useEffect(() => {
    if (operations.length > 0 && actionFromUrl === 'edit' && editIdFromUrl) {
      const opToEdit = operations.find(o => o.id === editIdFromUrl);
      if (opToEdit) {
        handleEdit(opToEdit);
      }
    }
  }, [operations, actionFromUrl, editIdFromUrl]);

  const fetchOperations = async () => {
    try {
      const response = await axios.get(`${API}/operations`);
      setOperations(response.data);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const byName = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', undefined, { sensitivity: 'base' });

  const fetchCabinets = async () => {
    try {
      const response = await axios.get(`${API}/cabinets`);
      setCabinets([...response.data].sort(byName('friendly_name')));
    } catch (error) {
      console.error('Failed to fetch cabinets:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`);
      setUsers([...response.data].sort(byName('nickname')));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    }
  };

  const fetchComponents = async () => {
    try {
      const response = await axios.get(`${API}/lists/component`);
      setComponents([...response.data].sort(byName('value')));
    } catch (error) {
      console.error('Failed to fetch components:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.cabinet_id || !formData.operation_type || !formData.description) {
      toast.error(t('errors.requiredField'));
      return;
    }

    // Validate price breakdown if provided
    if (formData.price_breakdown.length > 0) {
      const totalBreakdown = formData.price_breakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      
      let expectedTotal = 0;

      if (formData.operation_type === 'repair') {
          expectedTotal = parseFloat(formData.estimated_cost) || 0;
      } else if (formData.operation_type === 'purchase' || formData.operation_type === 'purchase_system') {
          expectedTotal = (parseFloat(formData.price) || 0) + (parseFloat(formData.transport_cost) || 0);
      } else {
          expectedTotal = parseFloat(formData.price) || 0;
      }

      if (Math.abs(totalBreakdown - expectedTotal) > 0.01) {
        toast.error(t('operations.totalMustEqual'));
        return;
      }
    }

    try {
      const payload = {
        cabinet_id: formData.cabinet_id,
        operation_type: formData.operation_type,
        date: formData.date,
        description: formData.description,
        price: formData.price ? parseFloat(formData.price) : null,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        transport_cost: formData.transport_cost ? parseFloat(formData.transport_cost) : null,
        price_breakdown: formData.price_breakdown.length > 0 ? formData.price_breakdown : null,
        affected_components: formData.affected_components.length > 0 ? formData.affected_components : null,
      };

      if (selectedOperation) {
        // Mode UPDATE
        await axios.put(`${API}/operations/${selectedOperation.id}`, payload);
      } else {
        // Mode CREATE
        await axios.post(`${API}/operations`, payload);
      }

      toast.success(t('common.success'));
      setShowCreateDialog(false);
      resetForm();
      navigate('/operations');

      fetchOperations();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const handleEdit = (op) => {
    setSelectedOperation(op);
    setFormData({
      cabinet_id: op.cabinet_id,
      operation_type: op.operation_type,
      date: op.date ? new Date(op.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      description: op.description,
      price: op.price || '',
      estimated_cost: op.estimated_cost || '',
      transport_cost: op.transport_cost || '',
      price_breakdown: op.price_breakdown || [],
      affected_components: op.affected_components || [],
    });
    setShowCreateDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedOperation) return;

    try {
      await axios.delete(`${API}/operations/${selectedOperation.id}`);
      toast.success(t('common.success'));
      setShowDeleteDialog(false);
      setSelectedOperation(null);
      fetchOperations();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const resetForm = () => {
    setFormData({
      cabinet_id: '',
      operation_type: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      price: '',
      estimated_cost: '', 
      transport_cost: '',
      price_breakdown: [],
      affected_components: [],
    });
  };

  const addInvestor = () => {
    setFormData({
      ...formData,
      price_breakdown: [...formData.price_breakdown, { user_id: '', nickname: '', amount: '' }],
    });
  };

  const updateInvestor = (index, field, value) => {
    const updated = [...formData.price_breakdown];
    updated[index][field] = value;
    if (field === 'user_id') {
      const user = users.find((u) => u.id === value);
      updated[index].nickname = user?.nickname || '';
    }
    setFormData({ ...formData, price_breakdown: updated });
  };

  const removeInvestor = (index) => {
    const updated = formData.price_breakdown.filter((_, i) => i !== index);
    setFormData({ ...formData, price_breakdown: updated });
  };

  const toggleComponent = (component) => {
    const current = formData.affected_components;
    const updated = current.includes(component)
      ? current.filter((c) => c !== component)
      : [...current, component];
    setFormData({ ...formData, affected_components: updated });
  };

  const getCabinetName = (cabinetId) => {
    const cabinet = cabinets.find((c) => c.id === cabinetId);
    return cabinet ? `${cabinet.friendly_name} (${cabinet.unique_id})` : cabinetId;
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
  const formatCurrency = (value) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);

  const getOperationColor = (type) => {
    switch (type) {
      case 'purchase': return 'bg-primary/15 text-primary border-primary/30';
      case 'sale': return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
      case 'purchase_system': return 'bg-violet-500/15 text-violet-400 border-violet-500/30';
      case 'repair': return 'bg-secondary/15 text-secondary border-secondary/30';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const filteredOperations = operations
    .filter((op) => {
      const matchesSearch = op.description.toLowerCase().includes(search.toLowerCase());
      const matchesType = filterType === 'all' || op.operation_type === filterType;
      const matchesCabinet = filterCabinet === 'all' || op.cabinet_id === filterCabinet;
      return matchesSearch && matchesType && matchesCabinet;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="operations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('operations.title')}
          </h1>
          <p className="text-muted-foreground mt-2 font-mono text-xs tabular-nums">
            {filteredOperations.length} {t('operations.title').toLowerCase()}
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreateDialog(true)} className="font-display uppercase font-bold tracking-[0.16em] glow-amber" data-testid="add-operation-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('operations.addOperation')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('operations.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 font-mono"
            data-testid="operation-search-input"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="filter-operation-type">
            <SelectValue placeholder={t('operations.filterByType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cabinets.all')}</SelectItem>
            <SelectItem value="purchase">{t('operations.purchase')}</SelectItem>
            <SelectItem value="purchase_system">{t('operations.purchaseSystem')}</SelectItem> 
            <SelectItem value="repair">{t('operations.repair')}</SelectItem>
            <SelectItem value="sale">{t('operations.sale')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCabinet} onValueChange={setFilterCabinet}>
          <SelectTrigger data-testid="filter-operation-cabinet">
            <SelectValue placeholder={t('operations.cabinet')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('cabinets.all')}</SelectItem>
            {cabinets.map((cabinet) => (
              <SelectItem key={cabinet.id} value={cabinet.id}>
                {cabinet.friendly_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Operations List */}
      {filteredOperations.length > 0 ? (
        <div className="space-y-3">
          {filteredOperations.map((op) => (
            <Card key={op.id} className="bezel hover:border-primary/50 transition-colors" data-testid={`operation-card-${op.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-md bg-muted border border-border flex items-center justify-center flex-shrink-0">
                      <Receipt className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium">{op.description}</p>
                      <Badge variant="primary" className="font-normal text-sm px-3 py-1 border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors cursor-default">
                           {getCabinetName(op.cabinet_id)}
                      </Badge>
                      <p className="text-xs text-muted-foreground font-mono">{formatDate(op.date)} • {op.created_by}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      {op.price && <p className="font-mono font-bold tabular-nums">{formatCurrency(op.price)}</p>}
                      {op.transport_cost && (
                        <p className="text-xs text-muted-foreground font-mono tabular-nums">
                          + {formatCurrency(op.transport_cost)} {t('operations.transportCost').toLowerCase()}
                        </p>
                      )}
                      {op.estimated_cost && (
                          <div className="text-xs font-mono font-bold tabular-nums" title="Estimated Cost">
                             (Est. {formatCurrency(op.estimated_cost)})
                          </div>
                      )}
                    </div>
                    <Badge className={`${getOperationColor(op.operation_type)} uppercase text-xs font-bold`}>
                      {t(`operations.${op.operation_type}`)}
                    </Badge>
                    <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/cabinets/${op.cabinet_id}`)}>
                            <Eye className="h-4 w-4" />
                        </Button>
                      {canEdit && (
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(op)}>
                          <Edit className="h-4 w-4" />
                      </Button>
                      )}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => { setSelectedOperation(op); setShowDeleteDialog(true); }}
                          data-testid={`delete-operation-${op.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bezel p-12 text-center">
          <Receipt className="w-16 h-16 mx-auto text-primary/40 mb-4" />
          <p className="text-muted-foreground">{t('common.noResults')}</p>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-operation-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-[0.08em] text-glow-amber">
              {selectedOperation ? t('operations.editOperation') : t('operations.addOperation')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Cabinet */}
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.cabinet')} *</Label>
              <Select value={formData.cabinet_id} onValueChange={(v) => setFormData({ ...formData, cabinet_id: v })}>
                <SelectTrigger data-testid="operation-cabinet-select">
                  <SelectValue placeholder={t('common.selectOption')} />
                </SelectTrigger>
                <SelectContent>
                  {cabinets.map((cabinet) => (
                    <SelectItem key={cabinet.id} value={cabinet.id}>
                      {cabinet.friendly_name} ({cabinet.unique_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.type')} *</Label>
               <Select 
                value={formData.operation_type} 
                onValueChange={(v) => setFormData({ ...formData, operation_type: v })}
                disabled={!!selectedOperation} 
              >
                <SelectTrigger data-testid="operation-type-select">
                  <SelectValue placeholder={t('common.selectOption')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase">{t('operations.purchase')}</SelectItem>
                  <SelectItem value="purchase_system">{t('operations.purchaseSystem')}</SelectItem> 
                  <SelectItem value="repair">{t('operations.repair')}</SelectItem>
                  <SelectItem value="sale">{t('operations.sale')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.date')} *</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-mono" data-testid="operation-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? format(new Date(formData.date), 'PPP') : t('common.selectOption')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date ? new Date(formData.date) : undefined}
                    onSelect={(date) => {
                      setFormData({ ...formData, date: format(date, 'yyyy-MM-dd') });
                      setCalendarOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.description')} *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('operations.description')}
                data-testid="operation-description-input"
              />
            </div>

            {/* Price fields for purchase/sale */}
            {(formData.operation_type === 'purchase' || formData.operation_type === 'sale' || formData.operation_type === 'purchase_system') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.price')} (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    data-testid="operation-price-input"
                  />
                </div>
                {(formData.operation_type === 'purchase' || formData.operation_type === 'purchase_system') && (
                  <div className="space-y-2">
                    <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.transportCost')} (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.transport_cost}
                      onChange={(e) => setFormData({ ...formData, transport_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid="operation-transport-input"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Affected Components (for repairs) */}
            {formData.operation_type === 'repair' && (
              <>
                <div className="space-y-2">
                  <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.affectedComponents')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {components.map((comp) => (
                      <Badge
                        key={comp.id}
                        variant={formData.affected_components.includes(comp.value) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleComponent(comp.value)}
                      >
                        {comp.value}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="eyebrow text-[10px] text-muted-foreground">Estimated Repair Cost (€)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder="Optional estimation"
                    value={formData.estimated_cost} 
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Predicted cost before actual expenses.</p>
                </div>
              </>
            )}

            {/* Price Breakdown / Investors (purchase, purchase_system, repair) */}
            {(formData.operation_type === 'purchase' || 
              formData.operation_type === 'purchase_system' || 
              formData.operation_type === 'repair') && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="eyebrow text-[10px] text-muted-foreground">{t('operations.priceBreakdown')}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addInvestor} data-testid="add-investor-btn">
                    <Plus className="w-4 h-4 mr-1" /> {t('operations.addInvestor')}
                  </Button>
                </div>
                {formData.price_breakdown.map((investor, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Select
                      value={investor.user_id}
                      onValueChange={(v) => updateInvestor(index, 'user_id', v)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={t('operations.investor')} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>{user.nickname}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      className="w-24"
                      placeholder="€"
                      value={investor.amount}
                      onChange={(e) => updateInvestor(index, 'amount', e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeInvestor(index)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreate} data-testid="create-operation-submit">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('operations.deleteOperation')}</AlertDialogTitle>
            <AlertDialogDescription>{t('cabinets.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive" data-testid="confirm-delete-operation">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Operations;
